import React from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';

import { DAYCARE, OVERNIGHT_BOARDING } from '@rover/constants/src/translations/services.constants';
import { AVAILABILITY_PREFERENCES_TYPE_IDS } from '@rover/constants/src/translations/sitterProfilePage.constants';
import AlertSuccess from '@rover/icons/svg/alert-success.svg';
import Calendar from '@rover/icons/svg/calendar.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { PersonCalendar } from '@rover/rsdk/src/apiClient/latest/model/personCalendar';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

import 'moment/min/locales';

import ServiceCapacity from './ServiceCapacity';

type Props = {
  sitterName: string;
  service: EnhancedPersonSitterProfileService;
  personCalendar: PersonCalendar;
};

// The fromNow() function rounds up days if they exceed 12 hours.

// CURRENT_WEEK = 7 days * 24hs + 12hs = 180hs.
const CURRENT_WEEK = 180;
// CURRENT_WEEK = 14 days * 24hs + 12hs = 348hs.
const PAST_WEEK = 348;

const getIcons = (theme: RoverTheme): Record<string, JSX.Element> => ({
  success: (
    <AlertSuccess
      width="16px"
      height="16px"
      fill={theme.textColor.success}
      color={theme.textColor.success}
    />
  ),
  warning: (
    <Calendar
      width="16px"
      height="16px"
      fill={theme.textColor.caution}
      color={theme.textColor.caution}
    />
  ),
  error: <Calendar width="16px" height="16px" fill={theme.textColor.error} textColor="error" />,
});

export const getIcon = (hours: number | undefined, theme: RoverTheme): JSX.Element => {
  const icons = getIcons(theme);
  if (hours !== undefined) {
    if (hours < CURRENT_WEEK) return icons.success;
    if (hours < PAST_WEEK) return icons.warning;
    if (hours >= PAST_WEEK) return icons.error;
  }
  return icons.error;
};

const LastUpdate = ({ personCalendar, sitterName, service }: Props): JSX.Element => {
  const { locale } = useI18nContext();
  const theme = useTheme();
  moment.locale(locale);

  const servicePreference = personCalendar.preferences?.find(
    (preference) => preference.calendarId === AVAILABILITY_PREFERENCES_TYPE_IDS()[service.slug]
  );

  let lastUpdateText = t`Calendar never updated`;
  let hoursSinceLastUpdate;

  if (personCalendar.updatedAt) {
    lastUpdateText = t`Calendar last updated ${moment(personCalendar.updatedAt).fromNow()}`;
    hoursSinceLastUpdate = moment().diff(moment(personCalendar.updatedAt), 'hours');
  }

  return (
    <>
      <Flex width="100%" justifyContent="flex-start" alignItems="center" pt="4x">
        {getIcon(hoursSinceLastUpdate, theme)}
        <Text size="100" fontWeight="semibold" ml="2x">
          {lastUpdateText}
        </Text>
      </Flex>
      {personCalendar.preferences &&
        servicePreference &&
        // @ts-expect-error TS is dumb and doesn't let us check if `slug` is one of the specific slugs we care about
        [DAYCARE().type, OVERNIGHT_BOARDING().type].includes(service.slug) && (
          <Box width="100%" pt="2x">
            <ServiceCapacity sitterName={sitterName} capacity={servicePreference} />
          </Box>
        )}
    </>
  );
};
export default LastUpdate;
